import { useInfiniteQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import ms from 'ms';

import { getComments } from 'hb-react/frontoffice/apps/CommentsV2/requests/getComments';
import type {
  CommentsQueryData,
  GetCommentsParams,
  GetCommentsResponse,
  PageParamType,
} from 'hb-react/frontoffice/apps/CommentsV2/types';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';

export const useComments = ({
  queryKey,
  params,
  enabled = true,
}: {
  queryKey: Array<unknown>;
  params: GetCommentsParams;
  enabled?: boolean;
}) => {
  const { currentUser } = useDefaultAppContexts();
  const userId = currentUser?.id as number;
  const { focusedCommentId } = params;

  return useInfiniteQuery<
    GetCommentsResponse,
    AxiosError,
    CommentsQueryData,
    Array<unknown>,
    PageParamType
  >({
    queryKey,
    queryFn: ({ pageParam, signal }) =>
      getComments({
        userId,
        params,
        pageParam,
        signal,
      }),
    enabled,
    // Focused Comment ID is stored in page params, so it can be used during re-fetching after invalidation.
    initialPageParam: { focusedCommentId },
    getNextPageParam: (lastPage) =>
      lastPage.has_next_page
        ? { afterCursor: lastPage.end_cursor, focusedCommentId: undefined }
        : undefined,
    getPreviousPageParam: (firstPage) =>
      firstPage.has_previous_page
        ? { beforeCursor: firstPage.start_cursor, focusedCommentId: undefined }
        : undefined,
    staleTime: ms('5m'),
  });
};
